import { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Alert } from 'flowbite-react';
import type { PhoneResponse } from '../../types/api';

interface EditPhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (phoneData: Partial<PhoneResponse>) => Promise<void>;
  phone?: PhoneResponse | null;
  title?: string;
}

export const EditPhoneModal: React.FC<EditPhoneModalProps> = ({
  isOpen,
  onClose,
  onSave,
  phone,
  title = 'Edit Phone'
}) => {
  const [formData, setFormData] = useState<Partial<PhoneResponse>>({
    phoneNumber: '',
    extension: '',
    phoneTypeDescriptorId: undefined,
    primaryPhoneIndicator: false
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (phone) {
      setFormData({
        phoneNumber: phone.phoneNumber || '',
        extension: phone.extension || '',
        phoneTypeDescriptorId: phone.phoneTypeDescriptorId,
        primaryPhoneIndicator: phone.primaryPhoneIndicator || false
      });
    } else {
      // Reset for new phone
      setFormData({
        phoneNumber: '',
        extension: '',
        phoneTypeDescriptorId: undefined,
        primaryPhoneIndicator: false
      });
    }
    setError(null);
  }, [phone, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save phone');
    } finally {
      setIsSaving(false);
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phoneNumber: formatted });
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="lg" popup>
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div>
            <Label htmlFor="phoneNumber">Phone Number *</Label>
            <TextInput
              id="phoneNumber"
              type="tel"
              value={formData.phoneNumber || ''}
              onChange={handlePhoneChange}
              required
              placeholder="(555) 123-4567"
              maxLength={14}
            />
          </div>

          <div>
            <Label htmlFor="extension">Extension</Label>
            <TextInput
              id="extension"
              value={formData.extension || ''}
              onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
              placeholder="1234"
              maxLength={10}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="primaryPhoneIndicator"
              checked={formData.primaryPhoneIndicator}
              onChange={(e) => setFormData({ ...formData, primaryPhoneIndicator: e.target.checked })}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <Label htmlFor="primaryPhoneIndicator">Set as primary phone</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button color="light" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : phone ? 'Save Changes' : 'Add Phone'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
